import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Grid,
} from "@mui/material";
import { customCategoriesForSport, resolveCustomCategory } from "../../utils/triviaArcade";

const QUESTION_COUNT_MAX = 20;

const getMaxQuestionsForConfig = () => QUESTION_COUNT_MAX;

const TriviaCustomConfigSheet = ({
  open,
  onClose,
  onStart,
  isStarting,
  sport = "mlb",
}) => {
  const [gameConfig, setGameConfig] = useState({
    category: "career_stats",
    difficulty: "medium",
    questionCount: 5,
    timeLimit: 30,
  });

  // Fix 3: clamp stored category whenever sport prop changes (no remount on route switch).
  useEffect(() => {
    setGameConfig((prev) => ({
      ...prev,
      category: resolveCustomCategory(sport, prev.category),
    }));
  }, [sport]);

  const handleChange = (field, value) => {
    setGameConfig((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "category" || field === "difficulty") {
        const maxQuestions = getMaxQuestionsForConfig(next);
        if (next.questionCount > maxQuestions) {
          next.questionCount = maxQuestions;
        }
      }
      return next;
    });
  };

  const handleStart = () => {
    onStart({ ...gameConfig, gameMode: "custom" });
  };

  return (
    <Drawer anchor="bottom" open={open} onClose={onClose}>
      <Box sx={{ p: 3, maxWidth: 560, mx: "auto", width: "100%" }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          Custom Game
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose your preferred settings to customize your trivia experience
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={gameConfig.category}
                label="Category"
                onChange={(e) => handleChange("category", e.target.value)}
              >
                {customCategoriesForSport(sport).map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={gameConfig.difficulty}
                label="Difficulty"
                onChange={(e) => handleChange("difficulty", e.target.value)}
              >
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography gutterBottom>
              Number of Questions: {gameConfig.questionCount} (max:{" "}
              {getMaxQuestionsForConfig(gameConfig)})
            </Typography>
            <Slider
              value={gameConfig.questionCount}
              onChange={(e, value) => handleChange("questionCount", value)}
              min={3}
              max={getMaxQuestionsForConfig(gameConfig)}
              step={1}
              marks
              valueLabelDisplay="auto"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography gutterBottom>
              Time Limit: {gameConfig.timeLimit} seconds
            </Typography>
            <Slider
              value={gameConfig.timeLimit}
              onChange={(e, value) => handleChange("timeLimit", value)}
              min={15}
              max={60}
              step={5}
              marks
              valueLabelDisplay="auto"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button fullWidth variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleStart}
            disabled={isStarting}
          >
            Start Game
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default TriviaCustomConfigSheet;
